import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { roleHierarchy } from '../../utils/roleUtils';
import InlineLoading from '../common/InlineLoading';

interface RoleGuardProps {
  children: React.ReactNode;
  requiredRole?: 'user' | 'admin' | 'super_admin';
  requireIntuitionAIDomain?: boolean;
}

const RoleGuard: React.FC<RoleGuardProps> = ({ 
  children, 
  requiredRole = 'user',
  requireIntuitionAIDomain = false
}) => {
  const { userData } = useAuth();

  // Wait for userData to be loaded before making role decisions
  // Show loading indicator to improve UX during authentication
  if (!userData) {
    return <InlineLoading message="Verifying permissions..." />;
  }

  // Check if user is from IntuitionAI domain (for super admin access)
  const isIntuitionAIUser = userData.email.endsWith('@intuitionai.co');
  
  // If IntuitionAI domain is required and user is not from that domain
  if (requireIntuitionAIDomain && !isIntuitionAIUser) {
    return <Navigate to="/dashboard" replace />;
  }

  const userRoleLevel = (roleHierarchy[userData.role as keyof typeof roleHierarchy] !== undefined) 
    ? roleHierarchy[userData.role as keyof typeof roleHierarchy] 
    : -1;
  const requiredRoleLevel = roleHierarchy[requiredRole];

  // Check if user has sufficient role level
  if (userRoleLevel < requiredRoleLevel) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default RoleGuard;